import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Twitch, Gamepad2, MonitorPlay, Volume2, Settings, Database } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const load = useCallback(async () => setSettings(await api.getSettings()), []);
  useEffect(() => { load(); }, [load]);

  const update = async (section, patch) => {
    setSettings((s) => ({ ...s, [section]: { ...s[section], ...patch } }));
    await api.saveSettings({ [section]: patch });
  };

  if (!settings) return null;
  const g = settings.general, a = settings.audio, tw = settings.twitch;

  return (
    <div className="p-5 md:p-8 max-w-4xl mx-auto">
      <PageHeader title="Impostazioni" subtitle="Configura app, overlay, audio e integrazioni." accent="amber" />

      <Section icon={Settings} title="Generale">
        <Row label="Lingua">
          <Select value={g.language} onValueChange={(v) => update("general", { language: v })}>
            <SelectTrigger className="w-40 bg-[#0b0e16] border-[#334155] text-white" data-testid="setting-language"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="it">Italiano</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
          </Select>
        </Row>
        <SliderRow label={`Durata animazioni: ${g.animationDuration}s`} value={g.animationDuration} min={2} max={10} onChange={(v) => update("general", { animationDuration: v })} testid="setting-anim-duration" />
        <SliderRow label={`Durata visualizzazione risultato: ${g.resultDuration}s`} value={g.resultDuration} min={2} max={15} onChange={(v) => update("general", { resultDuration: v })} testid="setting-result-duration" />
      </Section>

      <Section icon={Volume2} title="Audio">
        <Row label="Audio abilitato"><Switch checked={a.enabled} onCheckedChange={(v) => update("audio", { enabled: v })} data-testid="setting-audio-enabled" /></Row>
        <SliderRow label={`Volume: ${Math.round(a.volume * 100)}%`} value={Math.round(a.volume * 100)} min={0} max={100} onChange={(v) => update("audio", { volume: v / 100 })} testid="setting-audio-volume" />
        <Row label="Suono inizio rotazione"><Switch checked={a.start} onCheckedChange={(v) => update("audio", { start: v })} /></Row>
        <Row label="Tick della ruota"><Switch checked={a.tick} onCheckedChange={(v) => update("audio", { tick: v })} /></Row>
        <Row label="Suono risultato"><Switch checked={a.result} onCheckedChange={(v) => update("audio", { result: v })} /></Row>
      </Section>

      <Section icon={MonitorPlay} title="Overlay OBS">
        <p className="text-slate-400 text-sm mb-3">La configurazione completa dell'overlay e l'anteprima sono nell'editor dedicato.</p>
        <Link to="/overlay-editor"><Button className="bg-amber-500 hover:bg-amber-400 text-[#080a0f]" data-testid="goto-overlay-editor">Apri editor overlay</Button></Link>
      </Section>

      <Section icon={Twitch} title="Twitch">
        <Row label="Canale Twitch">
          <Input defaultValue={tw.channel} onBlur={(e) => update("twitch", { channel: e.target.value })} placeholder="il_tuo_canale" className="w-56 bg-[#0b0e16] border-[#334155] text-white" data-testid="setting-twitch-channel" />
        </Row>
        <Row label="Gira penitenza per nuovo abbonato"><Switch checked={tw.spinOnSub} onCheckedChange={(v) => update("twitch", { spinOnSub: v })} data-testid="setting-twitch-sub" /></Row>
        <Row label="Gira penitenza per resub"><Switch checked={tw.spinOnResub} onCheckedChange={(v) => update("twitch", { spinOnResub: v })} /></Row>
        <Row label="Gira penitenza per gift sub"><Switch checked={tw.spinOnGift} onCheckedChange={(v) => update("twitch", { spinOnGift: v })} /></Row>
        <Row label="Comportamento gift sub">
          <Select value={tw.giftMode} onValueChange={(v) => update("twitch", { giftMode: v })}>
            <SelectTrigger className="w-56 bg-[#0b0e16] border-[#334155] text-white" data-testid="setting-gift-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Una sola ruota per evento</SelectItem>
              <SelectItem value="per_gift">Una ruota per ogni gift</SelectItem>
              <SelectItem value="max">Massimo X ruote per evento</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <div className="mt-3 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 text-sm text-purple-200">
          <div className="flex items-center gap-2 font-semibold mb-1"><Twitch className="w-4 h-4" /> Collega Twitch</div>
          <p className="text-purple-200/80 text-xs">Per abilitare OAuth + EventSub reale, configura <code className="text-amber-300">TWITCH_CLIENT_ID</code> e <code className="text-amber-300">TWITCH_CLIENT_SECRET</code> nel file .env del backend. Nel frattempo usa "Simula abbonamento" nella pagina Streaming.</p>
          <Button className="mt-2 bg-purple-600 hover:bg-purple-500 text-white" data-testid="connect-twitch-button" onClick={() => toast.info("Configura le credenziali Twitch nel .env per abilitare il collegamento OAuth.")}>Collega Twitch</Button>
        </div>
      </Section>

      <Section icon={Gamepad2} title="Stream Deck">
        <p className="text-slate-400 text-sm mb-3">Genera la API Key e trova gli endpoint e le istruzioni del plugin.</p>
        <Link to="/stream-deck"><Button className="bg-amber-500 hover:bg-amber-400 text-[#080a0f]" data-testid="goto-streamdeck">Apri Stream Deck</Button></Link>
      </Section>

      <Section icon={Database} title="Backup">
        <p className="text-slate-400 text-sm mb-3">Esporta o importa tutta la configurazione (ruote, elementi, obiettivi) in JSON.</p>
        <Link to="/content"><Button variant="outline" className="border-slate-600 text-slate-200" data-testid="goto-backup">Vai a Gestione contenuti</Button></Link>
      </Section>

      <div className="text-center text-slate-500 text-xs mt-8 border-t border-[#1e293b] pt-6">
        Unofficial fan-made streaming utility. Not affiliated with or endorsed by Ubisoft.
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-[#131722] border border-[#334155] rounded-xl p-5 mb-5">
      <div className="flex items-center gap-2 font-display font-bold uppercase tracking-wide text-amber-400 mb-4">
        <Icon className="w-5 h-5" /> {title}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-slate-200 text-sm">{label}</span>
      {children}
    </div>
  );
}
function SliderRow({ label, value, min, max, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
      <Slider value={[value]} min={min} max={max} step={1} onValueChange={(v) => onChange(v[0])} className="mt-3" data-testid={testid} />
    </div>
  );
}
